import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMember } from '@/lib/use-session';
import { CirclesChrome } from '@/components/circles/CirclesChrome';
import { GroupsListBody } from '@/components/group/index';
import { groupSummaryToCircleSummary } from '@/components/circles/adapters';
import type { CircleSummary } from '@/components/circles/sample-data';
import {
  groupKeys,
  type ListCirclesArgs,
} from '@/lib/queries/groups';

const PAGE_SIZE = 24;
// The "mine" rail is a complete list, not a browse page — fetch a window
// large enough that no member's own circles are silently truncated.
const MINE_PAGE_SIZE = 100;

type Format = 'in_person' | 'virtual' | 'hybrid';
type Scope = 'all' | 'mine';

type CirclesSearch = {
  q?: string;
  topic?: string;
  format?: Format;
  language?: string;
  placeText?: string;
  placeCountryCode?: string;
  scope?: Scope;
};

const FORMATS = new Set<Format>(['in_person', 'virtual', 'hybrid']);
const SCOPES = new Set<Scope>(['all', 'mine']);

function parseFormat(v: unknown): Format | undefined {
  return typeof v === 'string' && FORMATS.has(v as Format) ? (v as Format) : undefined;
}
function parseScope(v: unknown): Scope | undefined {
  return typeof v === 'string' && SCOPES.has(v as Scope) ? (v as Scope) : undefined;
}
function pickStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute('/_public/circles/')({
  validateSearch: (raw: Record<string, unknown>): CirclesSearch => ({
    ...(pickStr(raw.q) !== undefined ? { q: pickStr(raw.q) as string } : {}),
    ...(pickStr(raw.topic) !== undefined ? { topic: pickStr(raw.topic) as string } : {}),
    ...(parseFormat(raw.format) !== undefined ? { format: parseFormat(raw.format) as Format } : {}),
    ...(pickStr(raw.language) !== undefined ? { language: pickStr(raw.language) as string } : {}),
    ...(pickStr(raw.placeText) !== undefined ? { placeText: pickStr(raw.placeText) as string } : {}),
    ...(pickStr(raw.placeCountryCode) !== undefined ? { placeCountryCode: pickStr(raw.placeCountryCode) as string } : {}),
    ...(parseScope(raw.scope) !== undefined ? { scope: parseScope(raw.scope) as Scope } : {}),
  }),
  component: CirclesListPage,
});

function CirclesListPage() {
  const search = Route.useSearch();
  const member = useMember();
  const signedIn = member !== null;

  // Browse defaults to the ranked discovery sort (completeness + viewer
  // similarity). An active search query switches to the server's recency
  // default. The server drops the similarity term for anonymous viewers, so
  // passing `sort: 'recommended'` is safe either way.
  const listArgs: ListCirclesArgs = {
    pageSize: PAGE_SIZE,
    ...(search.q === undefined ? { sort: 'recommended' as const } : {}),
    ...(search.q !== undefined ? { q: search.q } : {}),
    ...(search.topic !== undefined ? { topic: search.topic } : {}),
    ...(search.format !== undefined ? { format: search.format } : {}),
    ...(search.language !== undefined ? { language: search.language } : {}),
    ...(search.placeText !== undefined ? { placeText: search.placeText } : {}),
    ...(search.placeCountryCode !== undefined ? { placeCountryCode: search.placeCountryCode } : {}),
  };

  // Mine + pending drive the "My Circles" tab. Disabled when signed out: the
  // endpoints require auth and the data is irrelevant to the anon directory.
  const myCirclesQuery = useQuery({
    ...groupKeys.circle.mine({ includeArchived: true, pageSize: MINE_PAGE_SIZE }),
    enabled: signedIn,
  });
  const pendingQuery = useQuery({
    ...groupKeys.myPendingJoinRequests(),
    enabled: signedIn,
  });

  const mine: readonly CircleSummary[] | null = myCirclesQuery.data
    ? myCirclesQuery.data.items.map((m) =>
        groupSummaryToCircleSummary(m, { role: m.role }),
      )
    : null;

  const pendingCircles = pendingQuery.data?.filter((p) => p.group.kind === 'circle');
  const pending: readonly CircleSummary[] | null = pendingCircles
    ? pendingCircles.map((p) => groupSummaryToCircleSummary(p.group, { role: null }))
    : null;

  return (
    <CirclesChrome signedIn={signedIn}>
      <GroupsListBody
        kind="circle"
        listArgs={listArgs}
        mine={mine}
        pending={pending}
        isError={myCirclesQuery.isError || pendingQuery.isError}
        signedIn={signedIn}
      />
    </CirclesChrome>
  );
}
